/**
 * Markdown for chat bubbles.
 *
 * The web widget hands agent replies to `react-markdown` + `remark-gfm` and
 * lets Tailwind's prose plugin style the result. Neither of those crosses to
 * RN, and this package takes no dependency for it, so what lives here is a
 * renderer for the subset that actually appears in agent replies. Everything
 * else falls through as the literal text the model wrote — a reply must never
 * show half-parsed markup.
 *
 * Supported: paragraphs, `**bold**`, `*italic*`, inline code, `[links](url)`
 * (handed to `Linking`), bullet and ordered lists, `#` through `######`
 * headings, fenced code blocks, blockquotes (which may contain further
 * blocks), horizontal rules, and backslash escapes.
 *
 * Not supported, deliberately: TABLES — GFM's headline feature, but a
 * phone-width bubble has nowhere to put one, so a table degrades to a
 * paragraph of its pipe-separated source. Also absent: images (rendered as
 * their alt text), strikethrough, task lists, footnotes, reference links, raw
 * HTML, and nested list indentation (nested items flatten into their parent
 * list).
 *
 * Soft line breaks inside a paragraph collapse into spaces, which is what the
 * browser does for the web widget.
 */

import { Fragment, useMemo, type FC, type ReactNode } from "react";
import { Linking, Platform, StyleSheet, Text, View } from "react-native";

type HeadingLevel = 1 | 2 | 3 | 4;

/**
 * `strong` / `em` / `link` nest, so they carry children rather than a string:
 * `**bold with a [link](x)**` has to render as one styled run.
 */
type InlineToken =
  | { kind: "text"; value: string }
  | { kind: "code"; value: string }
  | { kind: "strong"; children: InlineToken[] }
  | { kind: "em"; children: InlineToken[] }
  | { kind: "link"; href: string; children: InlineToken[] };

type Block =
  | { kind: "paragraph"; content: InlineToken[] }
  | { kind: "heading"; level: HeadingLevel; content: InlineToken[] }
  | { kind: "code"; text: string }
  | { kind: "quote"; children: Block[] }
  | { kind: "list"; ordered: boolean; start: number; items: InlineToken[][] }
  | { kind: "rule" };

/** Typography resolved once per render and threaded down to every renderer. */
interface MarkdownTheme {
  color: string;
  mutedColor: string;
  linkColor: string;
  fontSize: number;
  lineHeight: number;
  codeFontSize: number;
  fontFamily: string | undefined;
}

interface BlocksProps {
  blocks: Block[];
  theme: MarkdownTheme;
}

interface MarkdownProps {
  content: string;
  /** Body text. */
  color: string;
  /** Chrome that is not text: the rule, the quote bar, the code outline. */
  mutedColor: string;
  linkColor: string;
  fontSize?: number;
  fontFamily?: string;
}

/** The widget's chat body size, matching the web bubble's `text-[15px]`. */
const DEFAULT_FONT_SIZE = 15;

/** The bubble's `leading-[1.3]`. */
const LINE_HEIGHT_RATIO = 1.3;

/**
 * prose renders code at ~0.86em; on the 15px chat body that lands on 12, which
 * is what the Flutter widget settles on too. Kept as a ratio so a caller that
 * overrides `fontSize` moves the code with it.
 */
const CODE_FONT_RATIO = 0.8;

/** Space between stacked blocks — prose's paragraph margin, tightened for a bubble. */
const BLOCK_GAP = 8;

const LIST_ITEM_GAP = 4;
const BULLET = "•";

const MONOSPACE_FONT = Platform.select({ ios: "Menlo", default: "monospace" });

const FENCE_PATTERN = /^\s{0,3}(`{3,}|~{3,})/;
const HEADING_PATTERN = /^\s{0,3}(#{1,6})\s+(.*)$/;
const RULE_PATTERN = /^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/;
const QUOTE_PATTERN = /^\s{0,3}>\s?(.*)$/;
const BULLET_PATTERN = /^(\s*)[-*+]\s+(.*)$/;
const ORDERED_PATTERN = /^(\s*)(\d{1,9})[.)]\s+(.*)$/;

/**
 * One alternation over every inline construct, so the tokenizer always takes
 * the leftmost one and never has to guess precedence between two passes.
 * Groups: 1 escaped char, 2 code, 3 emphasis delimiter, 4 its body, 5 image
 * bang, 6 link text, 7 href.
 *
 * `\3` forces the run of `*`/`_` that closes to be the run that opened, which
 * is what keeps `***both***` from leaving a stray asterisk behind. The `\S…\S`
 * body is CommonMark's "no space just inside the delimiter" rule — it is what
 * stops `2 * 3 * 4` from turning italic.
 */
const INLINE_PATTERN = /\\([\\`*_[\]()#+\-.!>])|`([^`]+)`|(\*{1,3}|_{1,3})(\S[\s\S]*?\S|\S)\3|(!?)\[([^\]]*)\]\(\s*<?([^)>\s]*)>?(?:\s+"[^"]*")?\s*\)/;

const HEADING_SCALE: Record<HeadingLevel, { sizeDelta: number; fontWeight: "600" | "700" }> = {
  // 20 / 18 / 16 / 15 against the 15px body — prose-sm's scale, and the same
  // steps the Flutter widget uses. h4 and below share the body size.
  1: { sizeDelta: 5, fontWeight: "700" },
  2: { sizeDelta: 3, fontWeight: "700" },
  3: { sizeDelta: 1, fontWeight: "600" },
  4: { sizeDelta: 0, fontWeight: "600" },
};

const isWordChar = (char: string | undefined): boolean => char !== undefined && /[A-Za-z0-9]/.test(char);

/** `*` italic, `**` bold, `***` both — CommonMark's emphasis nesting. */
const emphasis = (delimiters: number, children: InlineToken[]): InlineToken => {
  switch (delimiters) {
    case 1:
      return { kind: "em", children };
    case 2:
      return { kind: "strong", children };
    default:
      return { kind: "strong", children: [{ kind: "em", children }] };
  }
};

const headingLevel = (hashes: string): HeadingLevel => {
  switch (hashes.length) {
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
      return 3;
    default:
      return 4;
  }
};

const openUrl = (url: string): void => {
  // Best-effort: a malformed or unhandled scheme must not crash the widget.
  void Linking.openURL(url).catch(() => {});
};

const tokenizeInline = (source: string): InlineToken[] => {
  const tokens: InlineToken[] = [];
  // A fresh instance per call — this function recurses, and a shared
  // `lastIndex` would corrupt the scan it returns into.
  const pattern = new RegExp(INLINE_PATTERN.source, "g");
  let cursor = 0;

  const pushText = (value: string): void => {
    if (value) tokens.push({ kind: "text", value });
  };

  let match = pattern.exec(source);
  while (match !== null) {
    pushText(source.slice(cursor, match.index));
    cursor = match.index + match[0].length;

    const escaped = match[1];
    const code = match[2];
    const delimiter = match[3];
    const label = match[6];

    if (escaped !== undefined) {
      pushText(escaped);
    } else if (code !== undefined) {
      tokens.push({ kind: "code", value: code });
    } else if (delimiter !== undefined) {
      // `snake_case_name` is one word, not an emphasised middle. CommonMark
      // only opens `_` emphasis at a word boundary; `*` may open anywhere.
      if (delimiter.startsWith("_") && isWordChar(source[match.index - 1])) pushText(match[0]);
      else tokens.push(emphasis(delimiter.length, tokenizeInline(match[4] ?? "")));
    } else if (label !== undefined) {
      const href = match[7] ?? "";
      // An image has no place in a text run — RN cannot size a remote image
      // inline, and the alt text is what a screen reader would announce anyway.
      // A link with nothing to press (no href, or no label) is dropped whole.
      if (match[5] === "!" || !href || !label) pushText(label);
      else tokens.push({ kind: "link", href, children: tokenizeInline(label) });
    }

    match = pattern.exec(source);
  }

  pushText(source.slice(cursor));
  return tokens;
};

/** Whether a line would open a new block, used to end a paragraph run. */
const startsBlock = (line: string): boolean =>
  FENCE_PATTERN.test(line) || HEADING_PATTERN.test(line) || RULE_PATTERN.test(line) || QUOTE_PATTERN.test(line) || BULLET_PATTERN.test(line) || ORDERED_PATTERN.test(line);

const splitBlocks = (source: string): Block[] => {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index] ?? "";

    if (!line.trim()) {
      index += 1;
      continue;
    }

    const fence = FENCE_PATTERN.exec(line);
    if (fence !== null) {
      // Never fall back to "" — `startsWith("")` would close the block instantly.
      const marker = fence[1] ?? "```";
      const body: string[] = [];
      index += 1;
      while (index < lines.length) {
        const next = lines[index] ?? "";
        index += 1;
        if (next.trimStart().startsWith(marker)) break;
        body.push(next);
      }
      // An unterminated fence runs to the end of the message, as CommonMark says.
      blocks.push({ kind: "code", text: body.join("\n") });
      continue;
    }

    const heading = HEADING_PATTERN.exec(line);
    if (heading !== null) {
      blocks.push({ kind: "heading", level: headingLevel(heading[1] ?? ""), content: tokenizeInline((heading[2] ?? "").trim()) });
      index += 1;
      continue;
    }

    if (RULE_PATTERN.test(line)) {
      blocks.push({ kind: "rule" });
      index += 1;
      continue;
    }

    if (QUOTE_PATTERN.test(line)) {
      const quoted: string[] = [];
      while (index < lines.length) {
        const marker = QUOTE_PATTERN.exec(lines[index] ?? "");
        if (marker === null) break;
        quoted.push(marker[1] ?? "");
        index += 1;
      }
      // Every line shed its `>`, so the recursion is strictly smaller.
      blocks.push({ kind: "quote", children: splitBlocks(quoted.join("\n")) });
      continue;
    }

    const bullet = BULLET_PATTERN.exec(line);
    const numbered = bullet === null ? ORDERED_PATTERN.exec(line) : null;
    if (bullet !== null || numbered !== null) {
      const ordered = numbered !== null;
      const start = numbered === null ? 1 : Number(numbered[2] ?? "1");
      const items: string[] = [];
      while (index < lines.length) {
        const current = lines[index] ?? "";
        const item = ordered ? ORDERED_PATTERN.exec(current) : BULLET_PATTERN.exec(current);
        if (item !== null) {
          items.push((ordered ? item[3] : item[2]) ?? "");
          index += 1;
          continue;
        }
        // Lazy continuation: a plain wrapped line belongs to the item above it.
        const lastIndex = items.length - 1;
        const last = items[lastIndex];
        if (last !== undefined && current.trim() && !startsBlock(current)) {
          items[lastIndex] = `${last} ${current.trim()}`;
          index += 1;
          continue;
        }
        break;
      }
      blocks.push({ kind: "list", ordered, start, items: items.map((item) => tokenizeInline(item)) });
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index] ?? "";
      if (!current.trim()) break;
      // The first line already failed every block test above; the guard is what
      // guarantees the outer loop always consumes at least one line.
      if (paragraph.length > 0 && startsBlock(current)) break;
      paragraph.push(current.trim());
      index += 1;
    }
    blocks.push({ kind: "paragraph", content: tokenizeInline(paragraph.join(" ")) });
  }

  return blocks;
};

/**
 * Nested `<Text>` inherits colour and size from its parent, so only the
 * property each token changes is set here.
 */
const renderInline = (tokens: InlineToken[], theme: MarkdownTheme): ReactNode[] =>
  tokens.map((token, index) => {
    const key = `${token.kind}-${index}`;
    switch (token.kind) {
      case "text":
        return <Fragment key={key}>{token.value}</Fragment>;
      case "code":
        return (
          <Text key={key} style={[styles.code, { fontFamily: MONOSPACE_FONT, fontSize: theme.codeFontSize }]}>
            {token.value}
          </Text>
        );
      case "strong":
        return (
          <Text key={key} style={styles.strong}>
            {renderInline(token.children, theme)}
          </Text>
        );
      case "em":
        return (
          <Text key={key} style={styles.em}>
            {renderInline(token.children, theme)}
          </Text>
        );
      case "link":
        // A link inside a text run has to stay a `<Text>` — a Touchable cannot
        // live inside `<Text>` without breaking the line box.
        return (
          <Text key={key} accessibilityRole="link" style={[styles.link, { color: theme.linkColor }]} onPress={() => openUrl(token.href)}>
            {renderInline(token.children, theme)}
          </Text>
        );
      default:
        return null;
    }
  });

const Blocks: FC<BlocksProps> = ({ blocks, theme }) => {
  const body = { color: theme.color, fontSize: theme.fontSize, lineHeight: theme.lineHeight, fontFamily: theme.fontFamily };

  return (
    <View style={styles.stack}>
      {blocks.map((block, index) => {
        const key = `${block.kind}-${index}`;
        switch (block.kind) {
          case "paragraph":
            return (
              <Text key={key} style={body}>
                {renderInline(block.content, theme)}
              </Text>
            );
          case "heading": {
            const scale = HEADING_SCALE[block.level];
            const fontSize = theme.fontSize + scale.sizeDelta;
            return (
              <Text key={key} style={[body, { fontSize, lineHeight: fontSize * LINE_HEIGHT_RATIO, fontWeight: scale.fontWeight }]}>
                {renderInline(block.content, theme)}
              </Text>
            );
          }
          case "code":
            // Long lines wrap rather than scroll: a horizontal scroller nested
            // in the transcript's vertical one fights the user's gestures, and
            // the bubble is already capped at 80% of the panel.
            return (
              <View key={key} style={[styles.codeBlock, { borderColor: theme.mutedColor }]}>
                <Text style={{ color: theme.color, fontFamily: MONOSPACE_FONT, fontSize: theme.codeFontSize, lineHeight: theme.codeFontSize * LINE_HEIGHT_RATIO }}>{block.text}</Text>
              </View>
            );
          case "quote":
            return (
              <View key={key} style={[styles.quote, { borderLeftColor: theme.mutedColor }]}>
                <Blocks blocks={block.children} theme={theme} />
              </View>
            );
          case "list":
            return (
              <View key={key} style={styles.list}>
                {block.items.map((item, itemIndex) => (
                  <View key={`item-${itemIndex}`} style={styles.listItem}>
                    <Text style={body}>{block.ordered ? `${block.start + itemIndex}.` : BULLET}</Text>
                    <Text style={[body, styles.listItemText]}>{renderInline(item, theme)}</Text>
                  </View>
                ))}
              </View>
            );
          case "rule":
            return <View key={key} style={[styles.rule, { backgroundColor: theme.mutedColor }]} />;
          default:
            return null;
        }
      })}
    </View>
  );
};

export const Markdown: FC<MarkdownProps> = ({ content, color, mutedColor, linkColor, fontSize = DEFAULT_FONT_SIZE, fontFamily }) => {
  // The transcript re-renders on every store snapshot; parsing is keyed to the
  // message text so a typing indicator tick does not re-tokenize the history.
  const blocks = useMemo(() => splitBlocks(content), [content]);
  const theme = useMemo<MarkdownTheme>(
    () => ({ color, mutedColor, linkColor, fontSize, lineHeight: fontSize * LINE_HEIGHT_RATIO, codeFontSize: Math.round(fontSize * CODE_FONT_RATIO), fontFamily }),
    [color, mutedColor, linkColor, fontSize, fontFamily],
  );

  return <Blocks blocks={blocks} theme={theme} />;
};

const styles = StyleSheet.create({
  stack: { gap: BLOCK_GAP },
  strong: { fontWeight: "600" },
  em: { fontStyle: "italic" },
  /** prose renders inline code semibold, which is what separates it from prose text. */
  code: { fontWeight: "600" },
  link: { textDecorationLine: "underline" },
  codeBlock: { borderWidth: StyleSheet.hairlineWidth, borderRadius: 4, paddingHorizontal: 8, paddingVertical: 6 },
  quote: { borderLeftWidth: 4, paddingLeft: 12, paddingVertical: 4 },
  list: { gap: LIST_ITEM_GAP },
  listItem: { flexDirection: "row", gap: 6 },
  // Shrink rather than grow: the bubble hugs its content, and `flex: 1` would
  // collapse that intrinsic width to zero.
  listItemText: { flexShrink: 1 },
  rule: { height: StyleSheet.hairlineWidth, alignSelf: "stretch" },
});
